import { useEffect, useRef } from 'react'
import proj4 from 'proj4'
import Axios from 'axios'
import { WebMercatorViewport } from '@deck.gl/core'
import { useRecoilValue, useSetRecoilState } from 'recoil'

import { getImages } from 'data/common'
import {
  BATHYMETRY_CONFIG,
  getBathymetryTimesteps,
} from 'config/bathymetry'

import viewState from 'components/DeckGLMap/states/view'
import { activeModelState } from 'containers/Dilution/states/activeModel'
import {
  bathymetryVisibleState,
  bathymetryAvailableState,
  bathymetryImagesState,
  bathymetryLoadingState,
} from 'containers/Dilution/states/bathymetry'

/**
 * Fetches and manages the bathymetry BitmapLayer image.
 *
 * - Uses a hardcoded datetime (from config/bathymetry.js) as the /maps POST body key,
 *   since Bathymetry.dfsu always has a single timestep at a known datetime.
 * - Re-fetches whenever the map viewport (zoom/lat/lon) changes, but only when visible.
 * - Sets bathymetryAvailableState to false when the dfsu file is missing on the server.
 *
 * @param {{
 *   mapConnectionId: string,
 *   baseURL: string,
 * }} params - Values from the active model's first animation result (same connection as animation layers)
 */
const useBathymetryImages = ({ mapConnectionId, baseURL }) => {
  const view = useRecoilValue(viewState)
  const activeModel = useRecoilValue(activeModelState)
  const isVisible = useRecoilValue(bathymetryVisibleState)
  const setBathymetryImages = useSetRecoilState(bathymetryImagesState)
  const setBathymetryAvailable = useSetRecoilState(bathymetryAvailableState)
  const setBathymetryLoading = useSetRecoilState(bathymetryLoadingState)

  // Cancel token ref for in-flight /maps requests
  const cancelSourceRef = useRef(null)

  // Re-fetch on viewport pan/zoom, visibility toggle, model change, or connection change
  useEffect(() => {
    if (!isVisible || !mapConnectionId || !baseURL || !activeModel.id) return

    const source = Axios.CancelToken.source()
    if (cancelSourceRef.current) {
      cancelSourceRef.current.cancel()
    }
    cancelSourceRef.current = source

    const doFetch = async () => {
      setBathymetryLoading(true)
      try {
        // Compute viewport bbox in EPSG:3857
        const viewport = new WebMercatorViewport(view)
        const [north, west] = viewport.unproject([0, 0])
        const [south, east] = viewport.unproject([view.width, view.height])
        const [NORTH, WEST] = proj4('WGS84', 'EPSG:3857', [north, west])
        const [SOUTH, EAST] = proj4('WGS84', 'EPSG:3857', [south, east])
        const bounds = [north, east, south, west]

        const images = await getImages(
          {
            baseURL,
            timesteps: getBathymetryTimesteps(activeModel.id),
            mapConnectionId,
            mapStyle: BATHYMETRY_CONFIG.styleCode,
            itemNumber: BATHYMETRY_CONFIG.itemNumber,
            width: Math.round(view.width),
            height: Math.round(view.height),
            bbox: `${NORTH},${EAST},${SOUTH},${WEST}`,
            isMajorLoading: false,
            isoline: 'LineAndLabel',
          },
          source
        )

        // Bathymetry has a single image; tStep: 0 keeps it always visible
        const imagesWithBounds = Object.entries(
          images
        ).map(([timestep, image]) => [timestep, image, bounds, { tStep: 0 }])

        setBathymetryImages(imagesWithBounds)
        setBathymetryAvailable(true)
      } catch (error) {
        if (!Axios.isCancel(error)) {
          // The dfsu file is likely missing for this model
          setBathymetryAvailable(false)
          setBathymetryImages([])
          console.error(
            '[useBathymetryImages] Failed to fetch bathymetry:',
            error
          )
        }
      } finally {
        setBathymetryLoading(false)
      }
    }

    doFetch()

    return () => {
      if (cancelSourceRef.current) {
        cancelSourceRef.current.cancel()
      }
    }
  }, [
    view.zoom,
    view.latitude,
    view.longitude,
    isVisible,
    mapConnectionId,
    baseURL,
    activeModel.id,
  ])
}

export default useBathymetryImages
